import React, { useState, useEffect, useRef } from "react";
import {
  ChevronRight,
  ChevronLeft,
  User,
  Briefcase,
  Hash,
  Check,
  Loader2,
  Pencil,
  Plus,
  DollarSign,
  Clock,
  Layers,
  Receipt,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { clientsApi, usersApi, mattersApi, invoicesApi, taxSettingsApi } from "../../services/api";
import api from "../../services/api";
import SearchableSelect from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import QuickCreateClientModal from "../ui/QuickCreateClientModal";
import Toggle from "../ui/Toggle";
import { useGuidedTour } from "../../contexts/GuidedTourContext";
import { useUserDataContext } from "../../context/UserDataContext";
import {
  calculateTaxCents,
  getTaxLabel,
  DEFAULT_TAX_CONFIG,
  TaxConfig,
  TaxBreakdown,
} from "../../utils/taxCalculation";

// ─── Config Interface ────────────────────────────────────────────────────────

export interface CaseCreationConfig {
  practiceAreaKey: string;       // e.g. "criminal"
  apiBasePath: string;           // e.g. "criminal" -> POST /api/criminal/cases
  caseTypes: { value: string; label: string }[];
  /** When true the case endpoint creates the Matter too -- no pre-created matter from frontend */
  backendCreatesMatter?: boolean;
}

// ─── Component Props ─────────────────────────────────────────────────────────

interface Props {
  config: CaseCreationConfig;
  title: string;           // e.g. "Criminal Defence" -- shown in modal header
  icon: React.FC<any>;     // Lucide icon for the header
  onClose: () => void;
  onCreated: (matterId: string, meta?: { clientId: string; clientDisplayName: string; matterTitle: string }) => void;
}

const STEPS = [
  { num: 1, label: "Matter Details" },
  { num: 2, label: "Client & Team" },
  { num: 3, label: "Billing" },
  { num: 4, label: "Review" },
];

const FEE_TYPE_OPTIONS = [
  { value: "flat", label: "Flat Fee", icon: DollarSign },
  { value: "hourly", label: "Hourly", icon: Clock },
  { value: "mixed", label: "Mixed", icon: Layers },
];

const formatCAD = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

const CreatePracticeAreaCaseModal: React.FC<Props> = ({
  config,
  title: modalTitle,
  icon: ModalIcon,
  onClose,
  onCreated,
}) => {
  const { tour, completeTour } = useGuidedTour();
  const { userData } = useUserDataContext();
  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [showQuickCreateClient, setShowQuickCreateClient] = useState(false);
  const [showTourCloseWarning, setShowTourCloseWarning] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);
  const directionRef = useRef(1);

  const [form, setForm] = useState({
    caseType: config.caseTypes[0]?.value || "",
    title: "",
    matterNumber: "",
    clientId: "",
    responsibleUserId: userData._id || "",
    feeType: "flat" as "flat" | "hourly" | "mixed",
    flatFeeCents: 0,
  });

  // ── Billing / Invoice State ──
  const [legalFeeLabel, setLegalFeeLabel] = useState("Legal Fee");
  const [createInitialInvoice, setCreateInitialInvoice] = useState(false);
  const [invoiceMode, setInvoiceMode] = useState<"full" | "partial">("full");
  const [partialAmountDollars, setPartialAmountDollars] = useState("");
  const [dueDateOption, setDueDateOption] = useState<"1" | "7" | "30" | "custom">("30");
  const [customDueDate, setCustomDueDate] = useState("");
  const [sendInvoice, setSendInvoice] = useState(false);

  // ── Tax Config ──
  const [taxConfig, setTaxConfig] = useState<TaxConfig>(DEFAULT_TAX_CONFIG);

  // ── Load Clients, Users & Tax Settings ──
  useEffect(() => {
    Promise.all([
      clientsApi.list({ limit: 200, excludeSample: true }),
      usersApi.list({ limit: 200 }),
    ])
      .then(([cRes, uRes]) => {
        setClients(cRes.data.data || []);
        setUsers(uRes.data.data || []);
      })
      .catch(() => {});

    taxSettingsApi.get().then((res) => {
      const s = res.data;
      if (s && s.taxMode) {
        setTaxConfig({
          taxMode: s.taxMode,
          hstRate: s.hstRate ?? 13,
          gstRate: s.gstRate ?? 5,
          pstRate: s.pstRate ?? 0,
        });
      }
    }).catch(() => {});
  }, []);

  // ── Auto-update title when client is selected (only if not manually edited) ──
  useEffect(() => {
    if (titleManuallyEdited) return;
    const client = clients.find((c: any) => c._id === form.clientId);
    const caseTypeLabel = config.caseTypes.find((ct) => ct.value === form.caseType)?.label;
    if (client && caseTypeLabel) {
      setForm((prev) => ({ ...prev, title: `${caseTypeLabel}  -  ${client.displayName}` }));
    }
  }, [form.clientId, form.caseType, clients, config.caseTypes, titleManuallyEdited]);

  // ── Navigation ──
  const goToStep = (target: number) => {
    directionRef.current = target > step ? 1 : -1;
    setStep(target);
  };

  const goNext = () => {
    directionRef.current = 1;
    setStep(step + 1);
  };

  const goBack = () => {
    directionRef.current = -1;
    setStep(step - 1);
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return form.caseType && form.title.trim();
      case 2:
        return form.clientId && form.responsibleUserId;
      case 3:
        return true; // billing is optional
      case 4:
        return form.title.trim();
      default:
        return false;
    }
  };

  // ── Computed Totals ──
  const legalFeeCents = form.feeType !== "hourly" ? form.flatFeeCents : 0;
  const taxBreakdown: TaxBreakdown = calculateTaxCents(legalFeeCents, taxConfig);
  const grandTotalCents = legalFeeCents + taxBreakdown.totalTaxCents;
  const taxLabel = getTaxLabel(taxConfig);
  const partialCents = Math.round(parseFloat(partialAmountDollars || "0") * 100);
  const invoiceAmountCents = !createInitialInvoice
    ? 0
    : invoiceMode === "full"
    ? grandTotalCents
    : Math.min(partialCents, grandTotalCents);

  // ── Compute due date ──
  const computeDueDate = (): Date => {
    const d = new Date();
    if (dueDateOption === "custom" && customDueDate) {
      d.setTime(new Date(customDueDate).getTime());
    } else {
      d.setDate(d.getDate() + parseInt(dueDateOption));
    }
    return d;
  };

  const formatDueDate = (): string => {
    const d = computeDueDate();
    return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
  };

  // ── Submit ──
  const handleCreate = async () => {
    setCreating(true);
    setError("");
    try {
      let matterId: string;

      if (config.backendCreatesMatter) {
        const payload: Record<string, any> = {
          caseType: form.caseType,
          clientId: form.clientId,
          title: form.title,
          responsibleLawyerId: form.responsibleUserId,
          practiceAreas: [config.practiceAreaKey],
        };
        if (form.matterNumber.trim()) payload.matterNumber = form.matterNumber.trim();
        if (form.feeType !== "hourly" && form.flatFeeCents > 0) {
          payload.billing = { feeType: form.feeType, flatFeeCents: form.flatFeeCents };
        }

        const res = await api.post(`/api/${config.apiBasePath}/cases`, payload);
        matterId = res.data.matterId;
      } else {
        const matterPayload: Record<string, any> = {
          title: form.title,
          clientId: form.clientId,
          responsibleLawyerId: form.responsibleUserId,
          practiceAreas: [config.practiceAreaKey],
        };
        if (form.matterNumber.trim()) matterPayload.matterNumber = form.matterNumber.trim();

        const matterRes = await mattersApi.create(matterPayload);
        matterId = matterRes.data._id;

        try {
          await api.post(`/api/${config.apiBasePath}/cases`, { matterId, caseType: form.caseType, clientId: form.clientId });
        } catch (caseErr) {
          try { await mattersApi.remove(matterId); } catch (_) {}
          throw caseErr;
        }
      }

      // Create initial invoice if toggled on
      if (createInitialInvoice && invoiceAmountCents > 0) {
        try {
          const lineItems: any[] = [];

          if (invoiceMode === "full" && legalFeeCents > 0) {
            const feeTax = calculateTaxCents(legalFeeCents, taxConfig);
            lineItems.push({
              kind: "fee",
              description: `${legalFeeLabel}  -  ${form.title}`,
              quantity: 1,
              unitPriceCents: legalFeeCents,
              amountCents: legalFeeCents,
              taxable: true,
              taxAmountCents: feeTax.totalTaxCents,
            });
          } else if (invoiceMode === "partial" && partialCents > 0) {
            const cappedCents = Math.min(partialCents, grandTotalCents);
            const retainerTax = calculateTaxCents(cappedCents, taxConfig);
            lineItems.push({
              kind: "fee",
              description: `Retainer  -  ${form.title}`,
              quantity: 1,
              unitPriceCents: cappedCents,
              amountCents: cappedCents,
              taxable: true,
              taxAmountCents: retainerTax.totalTaxCents,
            });
          }

          if (lineItems.length > 0) {
            const dueDate = computeDueDate();
            const dueDateStr = isNaN(dueDate.getTime())
              ? new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10)
              : dueDate.toISOString().slice(0, 10);

            const invoiceRes = await invoicesApi.create({
              clientId: form.clientId,
              matterId,
              lineItems,
              dueAt: dueDateStr,
              internalNotes: `Auto-generated from ${modalTitle} matter: ${form.title}`,
            });

            if (sendInvoice && invoiceRes?.data?._id) {
              try {
                await invoicesApi.send(invoiceRes.data._id);
              } catch (sendErr) {
                console.error("Invoice created but failed to send:", sendErr);
              }
            }
          }
        } catch (invoiceErr) {
          console.error("Failed to create initial invoice:", invoiceErr);
        }
      }

      onCreated(matterId, {
        clientId: form.clientId,
        clientDisplayName: selectedClient?.displayName || "",
        matterTitle: form.title,
      });
    } catch (err: any) {
      const details = err.response?.data?.details;
      setError(
        (Array.isArray(details) && details.length > 0 ? details.join(", ") : null) ||
          err.response?.data?.error ||
          "Failed to create matter"
      );
    } finally {
      setCreating(false);
    }
  };

  // ── Derived Values ──
  const selectedClient = clients.find((c: any) => c._id === form.clientId);
  const selectedLawyer = users.find((u: any) => u._id === form.responsibleUserId);
  const selectedCaseTypeLabel = config.caseTypes.find((ct) => ct.value === form.caseType)?.label || form.caseType;

  const slideVariants = {
    enter: (dir: number) => ({ opacity: 0, x: dir * 20 }),
    center: { opacity: 1, x: 0 },
    exit: (dir: number) => ({ opacity: 0, x: dir * -20 }),
  };

  const handleClose = tour.isActive ? () => setShowTourCloseWarning(true) : onClose;

  return (
    <>
      {/* Tour close warning popup */}
      {showTourCloseWarning && (
        <div className="fixed inset-0 z-[10010] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowTourCloseWarning(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-6 p-6 text-center">
            <div className="h-12 w-12 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <Briefcase className="h-6 w-6 text-emerald-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Almost there!</h3>
            <p className="text-sm text-slate-500 leading-relaxed">
              Just finish creating this matter to complete the walkthrough. It only takes a moment and you can delete it after.
            </p>
            <button
              type="button"
              onClick={() => setShowTourCloseWarning(false)}
              className="mt-5 w-full px-5 py-3 rounded-xl text-white text-sm font-bold transition-colors"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              Continue creating matter
            </button>
            <button
              type="button"
              onClick={() => { completeTour(); onClose(); }}
              className="mt-2 w-full px-5 py-2.5 rounded-xl text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Skip walkthrough
            </button>
          </div>
        </div>
      )}

      <PremiumModal
        open={true}
        onClose={handleClose}
        title={`New ${modalTitle} Matter`}
        subtitle={`Step ${step} of 4`}
        icon={ModalIcon}
        size="lg"
        footer={
          <div className="flex justify-between w-full">
            <button
              type="button"
              onClick={step === 1 ? handleClose : goBack}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              {step === 1 ? "Cancel" : "Back"}
            </button>
            {step < 4 ? (
              <button
                type="button"
                disabled={!canProceed()}
                onClick={goNext}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
                style={{ backgroundColor: "var(--color-primary)" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--color-primary)")
                }
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                disabled={creating || !canProceed()}
                onClick={handleCreate}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
                style={{ backgroundColor: "var(--color-primary)" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--color-primary)")
                }
              >
                {creating ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating...
                  </span>
                ) : (
                  "Create Matter"
                )}
              </button>
            )}
          </div>
        }
      >
        {/* Tour hint */}
        {tour.isActive && (
          <div className="mb-4 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700 leading-relaxed">
            This is just a walkthrough  -  you can delete this matter later if you'd rather create a real one after.
          </div>
        )}

        {/* Step Progress Bar with Labels */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            {STEPS.map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold transition-all ${
                      s.num < step
                        ? "bg-brand-accent text-white"
                        : s.num === step
                        ? "bg-brand-accent text-white"
                        : "bg-slate-200 text-slate-400"
                    }`}
                  >
                    {s.num < step ? <Check className="h-4 w-4" /> : s.num}
                  </div>
                  <span
                    className={`text-xs transition-colors ${
                      s.num < step
                        ? "text-brand-accent font-medium"
                        : s.num === step
                        ? "text-brand font-semibold"
                        : "text-slate-400 font-medium"
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-px mx-2 mb-5 transition-colors ${
                      s.num < step ? "bg-brand-accent" : "bg-slate-200"
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content with Animated Transitions */}
        <AnimatePresence mode="wait" custom={directionRef.current}>
          <motion.div
            key={step}
            custom={directionRef.current}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <div className="space-y-4">
              {/* ── Step 1: Matter Details ── */}
              {step === 1 && (
                <>
                  <div className="flex items-center gap-3 pt-1">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Matter Type
                    </span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {config.caseTypes.map((ct) => (
                      <button
                        key={ct.value}
                        type="button"
                        onClick={() => setForm({ ...form, caseType: ct.value })}
                        className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all ${
                          form.caseType === ct.value
                            ? "border-brand-accent bg-brand-light text-brand"
                            : "border-slate-200 hover:border-slate-300 text-slate-600"
                        }`}
                      >
                        {ct.label}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Title
                    </span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>

                  <div>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        value={form.title}
                        onChange={(e) => {
                          setForm({ ...form, title: e.target.value });
                          setTitleManuallyEdited(true);
                        }}
                        placeholder="Matter title"
                        className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      A descriptive name for this matter
                    </p>
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Numbering
                    </span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Matter/File #
                    </label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="text"
                        value={form.matterNumber}
                        onChange={(e) => setForm({ ...form, matterNumber: e.target.value })}
                        placeholder="Auto-generated"
                        className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 -mt-2">
                    Leave blank to auto-generate
                  </p>
                </>
              )}

              {/* ── Step 2: Client & Team ── */}
              {step === 2 && (
                <>
                  <div className="flex items-center gap-3 pt-1">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Client
                    </span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>

                  {tour.isActive && (
                    <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-200">
                      <span className="text-sm text-amber-800">
                        Select an existing client or create a new one  -  use a real client or a test name for this walkthrough.
                      </span>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Client *
                    </label>
                    <SearchableSelect
                      options={clients.map((c: any) => ({
                        value: c._id,
                        label: c.displayName,
                      }))}
                      value={form.clientId || null}
                      onChange={(val) => setForm({ ...form, clientId: val || "" })}
                      placeholder="Select a client"
                      required
                      addNew={{
                        label: "Add new client",
                        onAdd: () => setShowQuickCreateClient(true),
                      }}
                    />
                    {selectedClient && (
                      <div className="flex items-center gap-2 mt-1.5 px-3 py-1.5 bg-brand-light rounded-lg text-xs font-medium text-brand">
                        <User className="h-3 w-3" />
                        {selectedClient.displayName}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Assignment
                    </span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Responsible Team Member *
                    </label>
                    <SearchableSelect
                      options={users.map((u: any) => ({
                        value: u._id,
                        label: u.name,
                      }))}
                      value={form.responsibleUserId || null}
                      onChange={(val) =>
                        setForm({ ...form, responsibleUserId: val || "" })
                      }
                      placeholder="Select a team member"
                      required
                    />
                  </div>
                </>
              )}

              {/* ── Step 3: Billing ── */}
              {step === 3 && (
                <>
                  {/* Fee Structure Selector */}
                  <div className="flex items-center gap-3 pt-1">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Fee Structure
                    </span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    {FEE_TYPE_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setForm({ ...form, feeType: opt.value as any })}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                          form.feeType === opt.value
                            ? "border-brand-accent bg-brand-light text-brand"
                            : "border-slate-200 hover:border-slate-300 text-slate-600"
                        }`}
                      >
                        <opt.icon className="h-5 w-5" />
                        <span className="text-sm font-medium">{opt.label}</span>
                      </button>
                    ))}
                  </div>

                  {/* Fee Amount Input */}
                  {form.feeType !== "hourly" && (
                    <>
                      <div className="flex items-center gap-3 pt-1">
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          {form.feeType === "mixed" ? "Flat Fee Portion" : "Fee Amount"}
                        </span>
                        <div className="flex-1 h-px bg-slate-100" />
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white p-3">
                        <div className="flex items-center gap-3">
                          <DollarSign className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                          <span className="flex-1 text-sm font-medium text-slate-800">{legalFeeLabel}</span>
                          <div className="relative w-32">
                            <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                            <input
                              type="number"
                              value={form.flatFeeCents / 100 || ""}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  flatFeeCents: Math.round(parseFloat(e.target.value || "0") * 100),
                                })
                              }
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              className="w-full rounded-lg border border-slate-200 pl-7 pr-2 py-2 text-sm text-right focus:outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500"
                            />
                          </div>
                        </div>

                        {/* Tax breakdown */}
                        {legalFeeCents > 0 && taxBreakdown.totalTaxCents > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-slate-400">Subtotal</span>
                              <span className="text-slate-500">{formatCAD(legalFeeCents)}</span>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-slate-400">{taxLabel}</span>
                              <span className="text-emerald-700 font-medium">{formatCAD(taxBreakdown.totalTaxCents)}</span>
                            </div>
                            <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                              <span className="text-sm font-semibold text-slate-700">Total</span>
                              <span className="text-sm font-semibold text-slate-900">{formatCAD(grandTotalCents)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {form.feeType === "hourly" && (
                    <p className="text-xs text-slate-400 italic">
                      Hourly rates will be configured on the matter after creation.
                    </p>
                  )}

                  {/* Initial Invoice Toggle */}
                  {legalFeeCents > 0 && (
                    <>
                      <div className="pt-1">
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                            Initial Invoice
                          </span>
                          <div className="flex-1 h-px bg-slate-100" />
                          <Toggle
                            checked={createInitialInvoice}
                            onChange={(val) => setCreateInitialInvoice(val)}
                          />
                        </div>
                      </div>

                      {createInitialInvoice && (
                        <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-3">
                          {/* Running total with tax */}
                          <div className="space-y-1.5 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-500">{legalFeeLabel}</span>
                              <span className="font-medium text-slate-700">{formatCAD(legalFeeCents)}</span>
                            </div>
                            {taxBreakdown.totalTaxCents > 0 && (
                              <div className="flex justify-between">
                                <span className="text-slate-500">{taxLabel}</span>
                                <span className="font-medium text-emerald-700">{formatCAD(taxBreakdown.totalTaxCents)}</span>
                              </div>
                            )}
                            <div className="border-t border-slate-200 pt-1.5 flex justify-between">
                              <span className="font-semibold text-slate-700">Total</span>
                              <span className="font-semibold text-slate-900">{formatCAD(grandTotalCents)}</span>
                            </div>
                          </div>

                          {/* Invoice mode selector */}
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-slate-500">How much to invoice?</p>
                            {[
                              { value: "full" as const, label: "Full amount", sub: formatCAD(grandTotalCents) },
                              { value: "partial" as const, label: "Partial / retainer", sub: null },
                            ].map((opt) => (
                              <label
                                key={opt.value}
                                className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                                  invoiceMode === opt.value
                                    ? "border-brand-light bg-brand-light"
                                    : "border-slate-200 bg-white hover:border-slate-300"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="invoiceMode"
                                  value={opt.value}
                                  checked={invoiceMode === opt.value}
                                  onChange={() => setInvoiceMode(opt.value)}
                                  className="accent-brand-accent"
                                />
                                <span className="flex-1 text-sm text-slate-700">{opt.label}</span>
                                {opt.sub && (
                                  <span className="text-xs font-medium text-slate-500">{opt.sub}</span>
                                )}
                              </label>
                            ))}

                            {invoiceMode === "partial" && (
                              <div className="relative ml-8">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                                <input
                                  type="number"
                                  value={partialAmountDollars}
                                  onChange={(e) => setPartialAmountDollars(e.target.value)}
                                  placeholder="0.00"
                                  step="0.01"
                                  min="0"
                                  className="w-full rounded-lg border border-slate-200 pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500"
                                />
                                {partialCents > grandTotalCents && grandTotalCents > 0 && (
                                  <p className="text-xs text-amber-600 mt-1">
                                    Amount exceeds total ({formatCAD(grandTotalCents)})
                                  </p>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Due date options */}
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-slate-500">Due date</p>
                            <div className="flex gap-2">
                              {[
                                { value: "1" as const, label: "1 Day" },
                                { value: "7" as const, label: "7 Days" },
                                { value: "30" as const, label: "30 Days" },
                                { value: "custom" as const, label: "Custom" },
                              ].map((opt) => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => setDueDateOption(opt.value)}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                    dueDateOption === opt.value
                                      ? "bg-brand-accent text-white"
                                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                            {dueDateOption === "custom" && (
                              <input
                                type="date"
                                value={customDueDate}
                                onChange={(e) => setCustomDueDate(e.target.value)}
                                min={new Date().toISOString().slice(0, 10)}
                                className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                              />
                            )}
                          </div>

                          <p className="text-xs text-slate-400">
                            A draft invoice for {formatCAD(invoiceAmountCents)}{taxBreakdown.totalTaxCents > 0 ? ` (incl. ${taxLabel})` : ""} will be created. Due{" "}
                            {formatDueDate()}.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {/* ── Step 4: Review & Create ── */}
              {step === 4 && (
                <>
                  <div className="flex items-center gap-3 pt-1">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      Summary
                    </span>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                    {/* Matter Type */}
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-2.5 text-sm text-slate-500">
                        <Briefcase className="h-4 w-4 text-slate-400" />
                        Matter Type
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">
                          {selectedCaseTypeLabel}
                        </span>
                        <button
                          type="button"
                          onClick={() => goToStep(1)}
                          className="text-xs text-brand-accent hover:text-brand font-medium flex items-center gap-0.5"
                        >
                          <Pencil className="h-3 w-3" />
                          Edit
                        </button>
                      </div>
                    </div>

                    {/* Title */}
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-2.5 text-sm text-slate-500">
                        <Briefcase className="h-4 w-4 text-slate-400" />
                        Title
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700 truncate max-w-[220px]">
                          {form.title}
                        </span>
                        <button
                          type="button"
                          onClick={() => goToStep(1)}
                          className="text-xs text-brand-accent hover:text-brand font-medium flex items-center gap-0.5"
                        >
                          <Pencil className="h-3 w-3" />
                          Edit
                        </button>
                      </div>
                    </div>

                    {/* Matter/File # */}
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-2.5 text-sm text-slate-500">
                        <Hash className="h-4 w-4 text-slate-400" />
                        Matter/File #
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">
                          {form.matterNumber.trim() || "Auto-generated"}
                        </span>
                        <button
                          type="button"
                          onClick={() => goToStep(1)}
                          className="text-xs text-brand-accent hover:text-brand font-medium flex items-center gap-0.5"
                        >
                          <Pencil className="h-3 w-3" />
                          Edit
                        </button>
                      </div>
                    </div>

                    {/* Client */}
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-2.5 text-sm text-slate-500">
                        <User className="h-4 w-4 text-slate-400" />
                        Client
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">
                          {selectedClient?.displayName}
                        </span>
                        <button
                          type="button"
                          onClick={() => goToStep(2)}
                          className="text-xs text-brand-accent hover:text-brand font-medium flex items-center gap-0.5"
                        >
                          <Pencil className="h-3 w-3" />
                          Edit
                        </button>
                      </div>
                    </div>

                    {/* Responsible Team Member */}
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-2.5 text-sm text-slate-500">
                        <Briefcase className="h-4 w-4 text-slate-400" />
                        Team Member
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">
                          {selectedLawyer?.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => goToStep(2)}
                          className="text-xs text-brand-accent hover:text-brand font-medium flex items-center gap-0.5"
                        >
                          <Pencil className="h-3 w-3" />
                          Edit
                        </button>
                      </div>
                    </div>

                    {/* Billing */}
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-2.5 text-sm text-slate-500">
                        <DollarSign className="h-4 w-4 text-slate-400" />
                        Fee Structure
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">
                          {FEE_TYPE_OPTIONS.find((o) => o.value === form.feeType)?.label || form.feeType}
                        </span>
                        <button
                          type="button"
                          onClick={() => goToStep(3)}
                          className="text-xs text-brand-accent hover:text-brand font-medium flex items-center gap-0.5"
                        >
                          <Pencil className="h-3 w-3" />
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Billing details below summary card */}
                  {legalFeeCents > 0 && (
                    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-600">{legalFeeLabel}</span>
                        <span className="text-slate-700 font-medium">{formatCAD(legalFeeCents)}</span>
                      </div>
                      {taxBreakdown.totalTaxCents > 0 && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-500">{taxLabel}</span>
                          <span className="text-emerald-700 font-medium">{formatCAD(taxBreakdown.totalTaxCents)}</span>
                        </div>
                      )}
                      <div className="border-t border-slate-200 pt-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-slate-700">Total</span>
                          <span className="text-sm font-bold text-slate-900">{formatCAD(grandTotalCents)}</span>
                        </div>
                      </div>

                      {createInitialInvoice && invoiceAmountCents > 0 && (
                        <div className="border-t border-slate-100 pt-3">
                          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3.5 py-2.5">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Receipt className="h-4 w-4 text-emerald-600" />
                                <span className="text-sm font-medium text-emerald-800">Initial Invoice</span>
                              </div>
                              <span className="text-sm font-bold text-emerald-800">{formatCAD(invoiceAmountCents)}</span>
                            </div>
                            <p className="text-xs text-emerald-600 mt-1">
                              {invoiceMode === "full" ? "Full amount" : "Partial / retainer"}  -  Due {formatDueDate()}
                            </p>
                            <label className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-emerald-200 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={sendInvoice}
                                onChange={(e) => setSendInvoice(e.target.checked)}
                                className="rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                              />
                              <span className="text-xs font-medium text-emerald-700">Send invoice to client on creation</span>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {error && (
                <div className="p-3 rounded-xl bg-red-50 text-red-700 text-sm">
                  {error}
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </PremiumModal>

      <QuickCreateClientModal
        open={showQuickCreateClient}
        onClose={() => setShowQuickCreateClient(false)}
        onCreated={(newClient) => {
          setClients((prev: any[]) => [...prev, newClient]);
          setForm((prev) => ({ ...prev, clientId: newClient._id }));
          setShowQuickCreateClient(false);
        }}
      />
    </>
  );
};

export default CreatePracticeAreaCaseModal;
